import React, { useEffect, useRef } from 'react';
import cornerstone from 'cornerstone-core';
import cornerstoneTools from 'cornerstone-tools';
import PropTypes from 'prop-types';

export const mprState = {
  axialIdx: 0, corIdx: 0, sagIdx: 0,
  ww: 400, wc: 40,
  depth: 1, rows: 1, columns: 1,
  listeners: new Set(),
  elements: {},
  notify() { this.listeners.forEach(fn => { try { fn(); } catch(e){} }); },
};
window.mprState = mprState;

// ── mprblob image loader ──────────────────────────────────────────────────────
// Pixels from worker are ALREADY windowed to 0-255 grayscale.
// We store them as Int16Array so cornerstone treats them as raw pixel values.
// Then set ww=256, wc=128 so cornerstone does NO additional windowing.
let loaderRegistered = false;
function ensureLoader() {
  if (loaderRegistered) return;
  loaderRegistered = true;

  cornerstone.registerImageLoader('mprblob', imageId => {
    const parts = imageId.replace('mprblob:', '').split('_');
    const idx  = parseInt(parts.pop(), 10);
    const type = parts.pop();
    const uid  = parts.join('_');

    const slices = window.__mprSliceCache && window.__mprSliceCache[uid + '_' + type];
    const slice  = slices && slices[idx];
    if (!slice) return { promise: Promise.reject(new Error('slice not found: ' + imageId)) };

    const { hu, w, h, scaledH } = slice;

    // Scale raw HU pixels to physical aspect ratio using nearest-neighbour
    // (no canvas needed — work directly in Int16 space)
    let pixels;
    if (scaledH === h) {
      // No scaling needed
      pixels = hu;
    } else {
      // Vertically scale: nearest-neighbour row sampling
      pixels = new Int16Array(w * scaledH);
      for (let row = 0; row < scaledH; row++) {
        const srcRow = Math.min(h - 1, Math.round(row * h / scaledH));
        const srcOff = srcRow * w;
        const dstOff = row * w;
        pixels.set(hu.subarray(srcOff, srcOff + w), dstOff);
      }
    }

    // Compute actual HU range for this slice
    let minPx = pixels[0], maxPx = pixels[0];
    for (let i = 1; i < pixels.length; i++) {
      if (pixels[i] < minPx) minPx = pixels[i];
      if (pixels[i] > maxPx) maxPx = pixels[i];
    }

    const image = {
      imageId,
      minPixelValue: minPx,
      maxPixelValue: maxPx,
      slope: 1,
      intercept: 0,
      windowWidth:  400,
      windowCenter: 40,
      rows: scaledH, columns: w,
      height: scaledH, width: w,
      color: false,
      columnPixelSpacing: 1, rowPixelSpacing: 1,
      sizeInBytes: w * scaledH * 2,
      getPixelData: () => pixels,
      render: cornerstone.renderGrayscaleImage,
    };

    return { promise: Promise.resolve(image) };
  });
}

// mprblob metadata provider — required for annotation tools to work
// CornerstoneTools calls cornerstone.metaData.get('imagePlaneModule', imageId)
// to get pixel spacing. Without it Length/Angle/Arrow crash or draw garbage.
let _mprMetaProviderRegistered = false;
function ensureMprMetaProvider() {
  if (_mprMetaProviderRegistered) return;
  _mprMetaProviderRegistered = true;
  cornerstone.metaData.addProvider((type, imageId) => {
    if (!imageId || !imageId.startsWith('mprblob:')) return undefined;
    const noPrefix = imageId.slice('mprblob:'.length);
    const parts    = noPrefix.split('_');
    const sliceIdx = parseInt(parts[parts.length - 1], 10);
    const orient   = parts[parts.length - 2];
    const uid      = parts.slice(0, parts.length - 2).join('_');
    const meta     = (window.__mprSliceCache || {})[uid + '_meta'] || {};
    const rowSp    = meta.rowSp   || 1;
    const colSp    = meta.colSp   || 1;
    const sliceSp  = meta.sliceSp || 1;

    if (type === 'imagePlaneModule') {
      let rowCosines, colCosines, rowPixelSpacing, columnPixelSpacing, imagePositionPatient;
      if (orient === 'axial') {
        rowCosines = [1,0,0]; colCosines = [0,1,0];
        rowPixelSpacing = rowSp; columnPixelSpacing = colSp;
        imagePositionPatient = [0, 0, sliceIdx * sliceSp];
      } else if (orient === 'cor') {
        rowCosines = [1,0,0]; colCosines = [0,0,-1];
        rowPixelSpacing = colSp; columnPixelSpacing = sliceSp;
        imagePositionPatient = [0, sliceIdx * rowSp, 0];
      } else {
        rowCosines = [0,1,0]; colCosines = [0,0,-1];
        rowPixelSpacing = rowSp; columnPixelSpacing = sliceSp;
        imagePositionPatient = [sliceIdx * colSp, 0, 0];
      }
      return {
        imagePositionPatient,
        imageOrientationPatient: [...rowCosines, ...colCosines],
        rowCosines, columnCosines: colCosines,
        rowPixelSpacing, columnPixelSpacing,
        sliceThickness: sliceSp,
        frameOfReferenceUID: 'mpr-for-' + uid,
      };
    }
    if (type === 'generalSeriesModule') return { modality: 'CT', seriesInstanceUID: 'mpr-' + uid + '-' + orient };
    if (type === 'imagePixelModule') return { samplesPerPixel:1, photometricInterpretation:'MONOCHROME2', bitsAllocated:16, bitsStored:16, highBit:15, pixelRepresentation:1 };
    if (type === 'voiLutModule') return { windowWidth:[256], windowCenter:[128] };
    if (type === 'modalityLutModule') return { rescaleSlope:1, rescaleIntercept:0, rescaleType:'HU' };
    return undefined;
  }, 10000);
}

// Cache built imageId arrays so the same reference is returned on every render.
// This prevents CornerstoneViewport's useEffect([imageIds,...]) from re-running
// and resetting the active tool every time the parent re-renders.
const _imageIdCache = {};
function buildImageIds(uid, type) {
  const key = uid + '_' + type;
  if (_imageIdCache[key]) return _imageIdCache[key];
  const slices = window.__mprSliceCache && window.__mprSliceCache[key];
  if (!slices) return [];
  _imageIdCache[key] = slices.map((_, i) => `mprblob:${uid}_${type}_${i}`);
  return _imageIdCache[key];
}

// ── Single cornerstone viewport — works for AX, COR, SAG ────────────────────
function CornerstoneViewport({ imageIds, label, orientation, uid, midIdx }) {
  const divRef     = useRef(null);
  const overlayRef = useRef(null);

  function getHFrac() {
    if (orientation === 'axial') return mprState.rows > 1 ? mprState.corIdx / (mprState.rows - 1) : 0.5;
    // COR/SAG: mprBuilder flips z so axialIdx=0→bottom, axialIdx=depth-1→top
    // Use cornerstone viewport transform to account for letterboxing/zoom/pan
    try {
      const el = divRef.current;
      if (el) {
        const enabledEl = cornerstone.getEnabledElement(el);
        if (enabledEl && enabledEl.image && enabledEl.viewport && enabledEl.canvas) {
          const img = enabledEl.image;
          const vp2 = enabledEl.viewport;
          const canvasEl = enabledEl.canvas;
          const depth = mprState.depth;
          const scaledH = img.height;
          // axialIdx=0 → bottom row (scaledH-1), axialIdx=depth-1 → top row (0)
          const imageRow = (scaledH - 1) - (mprState.axialIdx / Math.max(1, depth - 1)) * (scaledH - 1);
          const scale = vp2.scale || 1;
          const translation = (vp2.translation && vp2.translation.y) || 0;
          const canvasY = (imageRow - scaledH / 2) * scale + canvasEl.height / 2 + translation * scale;
          return canvasY / canvasEl.height;
        }
      }
    } catch(_e) {}
    return mprState.depth > 1 ? 1 - (mprState.axialIdx / (mprState.depth - 1)) : 0.5;
  }
  function getVFrac() {
    if (orientation === 'axial')   return mprState.columns > 1 ? mprState.sagIdx / (mprState.columns - 1) : 0.5;
    if (orientation === 'coronal') return mprState.columns > 1 ? mprState.sagIdx / (mprState.columns - 1) : 0.5;
    return mprState.rows > 1 ? mprState.corIdx / (mprState.rows - 1) : 0.5;
  }

  function redrawOverlay() {
    const ov = overlayRef.current;
    if (!ov) return;
    const cw = ov.width, ch = ov.height;
    const ctx = ov.getContext('2d');
    ctx.clearRect(0, 0, cw, ch);
    // Only draw ref lines if all 3 orientations are mounted (full 2D MPR mode)
    // In single-view (COR only / SAG only), skip ref lines
    const allMounted = mprState.elements['axial'] && mprState.elements['coronal'] && mprState.elements['sagittal'];
    if (!allMounted) return;
    const y = Math.round(getHFrac() * ch);
    ctx.strokeStyle = '#00e676'; ctx.lineWidth = 1.5; ctx.setLineDash([7, 4]);
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(cw, y); ctx.stroke();
    const x = Math.round(getVFrac() * cw);
    ctx.strokeStyle = '#ffd600'; ctx.lineWidth = 1.5; ctx.setLineDash([7, 4]);
    ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, ch); ctx.stroke();
  }

  useEffect(() => { mprState.listeners.add(redrawOverlay); return () => mprState.listeners.delete(redrawOverlay); }, []); // [] prevents duplicate listeners accumulating on every render

  useEffect(() => {
    const el = divRef.current;
    if (!el || !imageIds || !imageIds.length) return;

    ensureLoader();
    ensureMprMetaProvider();
    try { cornerstone.getEnabledElement(el); } catch(e) { cornerstone.enable(el); }
    mprState.elements[orientation] = el;

    // Register every tool the main OHIF toolbar can dispatch
    const TOOLS = [
      cornerstoneTools.WwwcTool,
      cornerstoneTools.WwwcRegionTool,
      cornerstoneTools.ZoomTool,
      cornerstoneTools.PanTool,
      cornerstoneTools.StackScrollTool,
      cornerstoneTools.StackScrollMouseWheelTool,
      cornerstoneTools.LengthTool,
      cornerstoneTools.EllipticalRoiTool,
      cornerstoneTools.RectangleRoiTool,
      cornerstoneTools.AngleTool,
      cornerstoneTools.ArrowAnnotateTool,
      cornerstoneTools.MagnifyTool,
      cornerstoneTools.DragProbeTool,
      cornerstoneTools.BidirectionalTool,
    ];
    TOOLS.forEach(T => { if (T) { try { cornerstoneTools.addToolForElement(el, T); } catch(e){} } });

    cornerstoneTools.setToolActiveForElement(el, 'Wwwc',                  { mouseButtonMask: 1 });
    cornerstoneTools.setToolActiveForElement(el, 'Pan',                   { mouseButtonMask: 4 });
    cornerstoneTools.setToolActiveForElement(el, 'Zoom',                  { mouseButtonMask: 2 });
    cornerstoneTools.setToolActiveForElement(el, 'StackScrollMouseWheel', {});

    const mid = midIdx !== undefined ? midIdx : Math.floor(imageIds.length / 2);
    // Guard: never call loadAndCacheImage with undefined imageId
    if (!imageIds[mid] || typeof imageIds[mid] !== 'string') return;
    const stack = { imageIds: [...imageIds].filter(Boolean), currentImageIdIndex: mid };
    try { cornerstoneTools.addStackStateManager(el, ['stack']); } catch(e) {}
    try { cornerstoneTools.addToolState(el, 'stack', stack); }    catch(e) {}

    cornerstone.loadAndCacheImage(imageIds[mid]).then(img => {
      try {
        cornerstone.displayImage(el, img);
        const initMeta = window.__mprSliceCache && window.__mprSliceCache[uid + '_meta'];
        if (orientation === 'axial') {
          // AX: reset zoom/pan then apply DICOM WW/WC
          cornerstone.reset(el);
          const initVp = cornerstone.getViewport(el);
          if (initMeta) {
            // Default to soft tissue (400/40) on open
            initVp.voi.windowWidth  = 400;
            initVp.voi.windowCenter = 40;
            cornerstone.setViewport(el, initVp);
          }
        } else {
          // COR/SAG now store raw HU — apply WL directly, same as axial
          const initVp = cornerstone.getViewport(el);
          initVp.voi.windowWidth  = 400;
          initVp.voi.windowCenter = 40;
          cornerstone.setViewport(el, initVp);
        }
        cornerstone.updateImage(el);
        redrawOverlay();
      } catch(e) {}
    });

    // Scroll → update crosshair state (RAF-throttled — prevents scroll flood)
    let _scrollRaf = null;
    const onNewImg = () => {
      if (_scrollRaf) return; // already queued, skip
      _scrollRaf = requestAnimationFrame(() => {
        _scrollRaf = null;
        try {
          const s = cornerstoneTools.getToolState(el, 'stack');
          if (!s || !s.data || !s.data[0]) return;
          const i = s.data[0].currentImageIdIndex;
          if (orientation === 'axial')    mprState.axialIdx = i;
          if (orientation === 'coronal')  mprState.corIdx   = i;
          if (orientation === 'sagittal') mprState.sagIdx   = i;
          mprState.notify();
        } catch(e) {}
      });
    };

    // WL drag on ANY viewport → sync same HU WL to all others
    // All viewports now store raw HU — no remap needed
    let _renderRaf = null;
    let _syncingWL = false; // prevent feedback loop
    const onRendered = () => {
      if (_renderRaf) return;
      _renderRaf = requestAnimationFrame(() => {
        _renderRaf = null;
        if (_syncingWL) return; // skip if we triggered this render ourselves
        try {
          const vp = cornerstone.getViewport(el);
          if (!vp || !vp.voi) return;
          const newWW = vp.voi.windowWidth;
          const newWC = vp.voi.windowCenter;
          // Only sync if WL actually changed
          if (Math.abs(newWW - mprState.ww) < 0.5 && Math.abs(newWC - mprState.wc) < 0.5) return;
          mprState.ww = newWW;
          mprState.wc = newWC;
          _syncingWL = true;
          Object.entries(mprState.elements).forEach(([orient, anyEl]) => {
            if (orient === orientation || !anyEl) return;
            try {
              const ovp = cornerstone.getViewport(anyEl);
              if (!ovp) return;
              ovp.voi.windowWidth  = newWW;
              ovp.voi.windowCenter = newWC;
              cornerstone.setViewport(anyEl, ovp);
              cornerstone.updateImage(anyEl);
            } catch(_e) {}
          });
          _syncingWL = false;
        } catch(e) { _syncingWL = false; }
      });
    };

    // WL preset buttons (1/2/3/4) - apply to ALL 3 viewports instantly
    const onWLPreset = e => {
      try {
        const { ww, wc } = e.detail;
        mprState.ww = ww; mprState.wc = wc;
        const meta = window.__mprSliceCache && window.__mprSliceCache[uid + '_meta'];
        const baseWW = (meta && meta.ww) || 400;
        const baseWC = (meta && meta.wc) || 40;
        Object.entries(mprState.elements).forEach(([orient, anyEl]) => {
          if (!anyEl) return;
          try {
            const ovp = cornerstone.getViewport(anyEl);
            if (!ovp) return;
            // All viewports (AX, COR, SAG) now store raw HU — apply directly
            ovp.voi.windowWidth  = ww;
            ovp.voi.windowCenter = wc;
            cornerstone.setViewport(anyEl, ovp);
            cornerstone.updateImage(anyEl);
          } catch(_e) {}
        });
      } catch(e) {}
    };

    // Toolbar tool activation
    // Restore last active tool if useEffect re-ran (e.g. after re-render)
    // _lastActiveTool persists across effect runs for this orientation
    if (!window.__mprLastTool) window.__mprLastTool = {};
    let _activeLMBTool = window.__mprLastTool[orientation] || 'Wwwc';
    // Re-apply the last active tool immediately so it isn't lost on re-render
    if (_activeLMBTool !== 'Wwwc') {
      try { cornerstoneTools.setToolActiveForElement(el, _activeLMBTool, { mouseButtonMask: 1 }); } catch(_e) {}
    }

    const onMprTool = e => {
      const { toolName, options: opts } = e.detail || {};
      if (!toolName) return;
      try {
        // Set previous left-click tool to passive so it stops intercepting
        // mouse events but keeps annotations visible
        if (_activeLMBTool && _activeLMBTool !== toolName) {
          try { cornerstoneTools.setToolPassiveForElement(el, _activeLMBTool); } catch(_e) {}
        }
        const options = opts || { mouseButtonMask: 1 };
        cornerstoneTools.setToolActiveForElement(el, toolName, options);
        if (!options.mouseButtonMask || options.mouseButtonMask === 1) {
          _activeLMBTool = toolName;
          // Persist so it survives effect re-runs
          if (!window.__mprLastTool) window.__mprLastTool = {};
          window.__mprLastTool[orientation] = toolName;
        }
      } catch(_e) {}
    };

    el.addEventListener('cornerstonenewimage',      onNewImg);
    el.addEventListener('cornerstoneimagerendered', onRendered);
    window.addEventListener('mprApplyWL',           onWLPreset);
    window.addEventListener('mprSetTool',           onMprTool);

    return () => {
      delete mprState.elements[orientation];
      // Cancel any pending RAF callbacks to avoid stale updates after unmount
      if (_scrollRaf) { cancelAnimationFrame(_scrollRaf); _scrollRaf = null; }
      if (_renderRaf) { cancelAnimationFrame(_renderRaf); _renderRaf = null; }
      el.removeEventListener('cornerstonenewimage',      onNewImg);
      el.removeEventListener('cornerstoneimagerendered', onRendered);
      window.removeEventListener('mprApplyWL',           onWLPreset);
      window.removeEventListener('mprSetTool',           onMprTool);
      try { cornerstone.disable(el); } catch(e) {}
    };
  }, [imageIds, uid, orientation]);

  return (
    <div style={{ width:'100%', height:'100%', position:'relative', background:'#000' }}>
      <div style={{ position:'absolute', top:6, left:8, color:'#4fc3f7', fontSize:11, fontWeight:'bold', zIndex:5, pointerEvents:'none' }}>{label}</div>
      <div ref={divRef} style={{ width:'100%', height:'100%' }} onContextMenu={e => {
        e.preventDefault();
        // Fire mprContextMenu so MeasurementsPanel/init.js can show UIDialogService menu
        window.dispatchEvent(new CustomEvent('mprContextMenu', {
          detail: { x: e.clientX, y: e.clientY }
        }));
      }} />
      <canvas ref={overlayRef} width={512} height={512}
        style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', pointerEvents:'none', zIndex:4 }}
      />
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
function Simple2DMPRViewport({ imageIds, orientation, displaySetUID }) {
  const uid  = displaySetUID || '';
  const meta = window.__mprSliceCache && window.__mprSliceCache[uid + '_meta'];

  if (meta && mprState.depth === 1) {
    Object.assign(mprState, {
      depth: meta.depth, rows: meta.rows, columns: meta.columns,
      ww: meta.ww, wc: meta.wc,
      axialIdx: meta.axialMid, corIdx: meta.corMid, sagIdx: meta.sagMid,
    });
  }

  if (orientation === 'axial') {
    if (!imageIds || !imageIds.length || !imageIds[0]) return null;
    return (
      <CornerstoneViewport imageIds={imageIds} label="AX" orientation="axial" uid={uid}
        midIdx={meta ? meta.axialMid : Math.floor(imageIds.length / 2)} />
    );
  }

  if (orientation === 'coronal') {
    const ids = buildImageIds(uid, 'cor');
    if (!ids || !ids.length || !ids[0]) return null;
    return <CornerstoneViewport imageIds={ids} label="COR" orientation="coronal" uid={uid}
      midIdx={meta ? meta.corMid : Math.floor((ids.length || 2) / 2)} />;
  }

  if (orientation === 'sagittal') {
    const ids = buildImageIds(uid, 'sag');
    if (!ids || !ids.length || !ids[0]) return null;
    return <CornerstoneViewport imageIds={ids} label="SAG" orientation="sagittal" uid={uid}
      midIdx={meta ? meta.sagMid : Math.floor((ids.length || 2) / 2)} />;
  }
  return null;
}

Simple2DMPRViewport.propTypes = {
  imageIds:      PropTypes.arrayOf(PropTypes.string).isRequired,
  orientation:   PropTypes.oneOf(['axial','coronal','sagittal']).isRequired,
  displaySetUID: PropTypes.string,
};
export default Simple2DMPRViewport;
